/* Respondent Details Card */
import React from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import geoJson from "../data/bd-geo-units.json";
import { PersonRecord } from "../types/data";
import { convertInCurrentLang, convertNumberInCurrentLang } from "../utils/misc";


const RespondentDetailCard = ({
  person
}: {
  person: PersonRecord;
}) => {
  const navigate = useNavigate();
  const { t, lang } = useLang();

  const lastLog = person.records?.[person.records.length - 1];
  const addedOn = person.added_on ? new Date(person.added_on) : null;

  const districts = geoJson.divisions.flatMap((div) => div.districts);

  const getDistrictName = (districtId: string): string => {
    const district = districts.find((d) => d.id === districtId);
    return district
      ? lang === "bn"
        ? district.name_bn
        : district.name_en
      : "";
  };

  const getUpazilaName = (upazilaId: string, districtId: string): string => {
    const district = districts.find((d) => d.id === districtId);
    const upazila = district?.upazilas.find((u) => u.id === upazilaId);
    return upazila ? (lang === "bn" ? upazila.name_bn : upazila.name_en) : "";
  };

  const getUnionName = (
    unionId: string,
    upazilaId: string,
    districtId: string
  ): string => {
    const district = districts.find((d) => d.id === districtId);
    const upazila = district?.upazilas.find((u) => u.id === upazilaId);
    const union = upazila?.unions.find((u) => u.id === unionId);
    return union ? (lang === "bn" ? union.name_bn : union.name_en) : "";
  };

  const formattedAddress = () => {
    if (!person.address) return "";
    const parts = [];
    if (person.address.village) parts.push(person.address.village);
    if (person.address.union) parts.push(getUnionName(person.address.union, person.address.upazila ?? "", person.address.district ?? ""));
    if (person.address.upazila) parts.push(getUpazilaName(person.address.upazila, person.address.district ?? ""));
    if (person.address.district) parts.push(getDistrictName(person.address.district));
    return parts.join(", ");
  }

  return (
    <div
      key={person.id}
      className="flex justify-between items-center p-4 rounded-2xl bg-slate-300 bg-opacity-20 backdrop-blur-md "
    >
      <div className="flex flex-col items-start flex-1 text-left justify-start space-y-1">
        <p className="font-semibold text-3xl text-blue-700">{person.name}</p>
        <div className="rounded-full flex items-center bg-slate-600 text-white border text-lg font-bold text-gray-700 px-3 min-h-8 shrink-0">
          {!person.sex && <span>{t("age")}:&nbsp;</span>} {convertNumberInCurrentLang(lang, person.age ? person.age : 0)}
          {person.sex && <span>, {t(person.sex ? person.sex : "other")}</span>}
        </div>
        {person.mobile && (
                      <div className="flex items-center gap-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="2"
                          stroke="currentColor"
                          className="size-6"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3"
                          />
                        </svg>

                        <span className="text-lg font-bold text-gray-600">
                          {" "}
                          {convertInCurrentLang(lang, person.mobile)}
                        </span>
                      </div>
                    )}
        {person.occupation && (
                      <p className="text-lg font-semibold text-gray-600">
                        {t("occupation")}: {t(person.occupation)}
                      </p>
                    )}

        {person.address && (
          <p className="text-lg font-semibold text-gray-600">
            {formattedAddress()}
          </p>
        )}

        <p className="text-lg text-blue-500 mt-1">
          <span className="font-bold">
            {convertNumberInCurrentLang(lang, person.records?.length || 0)}
          </span>
          {lang === "bn" ? " দিনের লগ করা হয়েছে" : " day(s) logged"}
        </p>
        {lastLog && (
          <p className="text-xs text-gray-600">
            {lang === "bn" ? "সর্বশেষ লগ: " : "Last log: "}
            <span className="">
              {convertInCurrentLang(
                lang,
                new Date(lastLog.date).toLocaleDateString(lang === "bn" ? "bn-BD" : "en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              )}
            </span>
          </p>
        )}
        {
          addedOn && (
            <p className="text-xs text-gray-500 mt-1">
              {lang === "bn" ? "যোগ করা হয়েছে: " : "Added on: "}
              <span className="">
                {convertInCurrentLang(
                  lang, 
                  addedOn.toLocaleDateString(lang === "bn" ? "bn-BD" : "en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                )}
              </span>
            </p>
          )
        }
      </div>  
    </div>
  );
};

export default RespondentDetailCard;
